import type { Suggestion } from "./types";
import type { TapEntry } from "./route-source";
import { favouriteKind } from "./favourites";
import type { FavouritesSnapshot, FavouriteKind } from "./favourites";
import { suggestionDims, normalizeSport, oddsBand } from "./event-dims";
import type { EventDims } from "./event-dims";
import { partSuffix } from "./bb-space";

// ── Placement attribution v2 — the client-side bet ledger ─────────────────────
//
// Digitain's ENTIRE betslip lifecycle rides the same `sender:"http-api-response"`
// postMessage stream that DigitainFeedSource already listens on for odds, so this
// costs no new transport and no extra request. Measured live on Parikara
// 2026-07-30 with real bets (see docs/digitain/attribution-v2.md):
//
//   /Betting/AddStake           per-add ack: Status "Success" | "ERROR_STAKE_DOS_NOT_EXIST"
//   /Betting/RemoveStake        leg removed
//   /Betting/UpdateCoupon       slip edited
//   /Betting/ChangeBetType      Ординар ↔ Экспресс ↔ Система
//   /Betting/UpdateLocalStorage carries the full CheckedStakesList
//   /Betting/GetCouponForUpdate an EDIT session opens (IsBetEdit:true)
//   /Betting/PlaceBet           receipt — a FRESH placement
//   /Betting/PlaceBetPlus       receipt — an EDITED bet
//
// This replaces the v1 model (`attribution.ts`), which keyed on a contentless
// `successBet` pulse from the sportsbook iframe and credited any placement inside
// a 10-minute window. That pulse does NOT fire on Parikara at all (no sportsbook
// iframe), and it could never say WHICH bet was placed.
//
// Three measured facts drive the design below — each one is a trap:
//
//  1. `BetFactor`/`PossibleWin` are ALWAYS 0 in the receipt (verified on both a
//     single and a combo). The combined price must be computed as Π(factor) over
//     CheckedStakesList. Never read BetFactor.
//  2. `IsBetEdit` is TRUE throughout an edit session but FALSE on the confirming
//     PlaceBetPlus. So an edit CANNOT be detected from the receipt — we track a
//     session flag opened by GetCouponForUpdate instead.
//  3. `OrderNumber` is REUSED by an edit (the edited bet confirms under the
//     original bet's order number). It identifies the BET, not the event, so
//     downstream storage must UPSERT on it: dedup-and-drop loses the edit,
//     append-only double-counts the stake.

/** How the placed coupon relates to what we recommended. */
export type AttributionClass = "ours_exact" | "ours_partial" | "organic";

/** Outcome of waiting for the operator to acknowledge an add.
 *  - `confirmed` — every stake we posted is in the server's own slip list
 *  - `partial`   — SOME landed. Not a refusal: the user is holding those legs,
 *                  so the caller must sweep exactly them and forget the rest
 *  - `rejected`  — the operator answered and NONE of our stakes landed
 *  - `no-bus`    — this operator has never sent any /Betting/ traffic, so it has
 *                  no ack channel (Hannibalbet) and the caller falls back to the
 *                  mint probe. Silence from an operator whose bus we HAVE seen is
 *                  not this — it is classified from the slip. */
export type AddVerdict = "confirmed" | "partial" | "rejected" | "no-bus";

/** How a confirmAdd verdict was reached. `declined` carries the operator's own
 *  status string; every other path has no operator answer to quote. `store` means
 *  no message arrived at all and the answer came from the app's OWN redux coupon
 *  (`coupon-store.ts`) — the same authority the bus messages carry, read directly
 *  because the channel went quiet. */
export interface AckResolution {
  how: "immediate" | "ack" | "declined" | "timeout" | "no-bus" | "store";
  status: string | null;
}

/** How a leg came to be in the coupon.
 *  - `widget_add` — WE put it there (`recordAdd`). Causal.
 *  - `surfaced`   — we SHOWED it and the user tapped through to the operator's own
 *                   promo card (`recordSurfaced`). Correlational — Digitain shows
 *                   the same promo natively, so this must never be counted as ours.
 *  - `navigated`  — we SENT THEM TO THE MATCH (`recordNavigate`) and they bet on it.
 *                   Weakest of the three, and the only one keyed on EVENT rather
 *                   than stake: the whole point of sending someone to a match is
 *                   that they then choose for themselves, so the stake they take is
 *                   usually NOT the one on the card. Correlational — they may well
 *                   have reached the match on their own — so like `surfaced` it
 *                   feeds `influenced` and never `attribution`.
 *  Absent on a leg the user found by themselves. */
export type LegSource = "widget_add" | "surfaced" | "shown" | "navigated";
// `shown` — the coupon holds the SAME outcome (event, market, code, line) a card
// of ours showed this visitor, under a stake id we did not insert: they removed
// and re-added it by hand, our add was refused, or the book rotated the id. The
// pick was ours; the price they took is theirs. Between `surfaced` and
// `navigated` in precedence, and like them it never feeds `attribution`.

export interface PlacedLeg {
  stake_id: number;
  /** The price the operator actually ACCEPTED (may differ from what we showed). */
  accepted_odds: number | null;
  /** Seconds this stake had been in the current betslip when placed. Absent when
   * the watcher does not know when the stake entered the slip. */
  slip_age_s?: number;
  /** True only when the stake was already in the first slip snapshot this watcher
   * saw, so its age is a lower bound rather than the true time in the slip. */
  slip_age_floor?: true;
  entry?: LegEntry;
  /** Is this leg on something the visitor starred — absent when we hold no
   * favourites snapshot, so "unknown" never reads as "none". */
  fav?: FavouriteKind;
  /** Absent for the user's own legs. */
  source?: LegSource;
  /** Set only when this leg is one we recommended. */
  suggestion_id?: string;
  /** The Digitain event. Present on EVERY leg — ours, navigated, or the user's
   * own — because the coupon states `EventId` per leg regardless of how it got
   * there, and regardless of whether the match is live or pre-match. ⛔ Do NOT
   * read its presence as "we recommended this"; `source` says that. */
  event_id?: string;
  /** The price WE showed. Null/absent unless this leg is one we recommended. */
  rec_odds?: number | null;
  /** Human-readable pick ("П1", a team name) so the dashboard can name what was
   * placed — a stake id alone is unreadable to an operator. */
  selection?: string | null;
  /** ABSOLUTE kick-off instant, present only when this leg was placed on a
   * fixture that had NOT started. Absent on a live leg, and absent on a leg
   * neither board could name — absent is UNKNOWN, never "it was live". */
  starts_at?: string;
  /** WHICH MARKET the leg is, on EVERY leg — ours and the user's own.
   *
   * `market_id` is Digitain's `StakeTypeId` ("match winner", "total goals",
   * "first half total"…), `stake_code` the outcome inside it (home / over /
   * under…), and `argument` the LINE where the market has one — 2.5 on an
   * over/under, −1.5 on a handicap.
   *
   * ⛔ Added 2026-08-28 because none of it was stored. All three have been on
   * the wire the whole time — `CheckedStakesList` states them per leg and the
   * dead-leg repair path already read them — but a placement recorded only a
   * stake id and a price, so "do they bet the moneyline or over 2.5 in the
   * first half" was unanswerable about the book we have been watching for
   * weeks.
   *
   * ⚠️ IDS, not names. The name lives on the board's `StakeType`, which we do
   * not hold for a market nobody surfaced, so the backend maps the ids it knows
   * and reports the rest as ids rather than inventing labels. */
  market_id?: number;
  stake_code?: number;
  /** The line. `null` where the market has none (a moneyline has no argument),
   * which is itself information — so null is stored and absent means we could
   * not read the field at all. */
  argument?: number | null;
  dims?: EventDims;
  /** `"<EventPartTypeId>:<PeriodId>"` when the leg is on a sub-event (corners,
   * cards, a half); absent on the main book. */
  part?: string;
}

export type LegEntry = Omit<TapEntry, "stake_id" | "event_id" | "card_stake_ids">;

/** What the board we already hold knows about a stake id, with no event id given —
 * used to describe a leg the user found on their own, which the receipt names only
 * by stake id and accepted price. */
export interface StakeFacts {
  sport_en?: string;
  league_en?: string;
  country_en?: string;
  is_live?: boolean;
  selection?: string | null;
}

/** What the boards know about an EVENT id.
 *
 * ⛔ Resolved by EVENT id, not stake id, and that is the point. A stake id names
 * a PRICE — a market taken down and re-opened mints a new one — so a stake
 * lookup misses exactly when the market moved. `EventId` is on every coupon leg
 * and is stable, so a leg is nameable long after its stake id has rotated.
 *
 * `starts_at` is present only for a pre-match fixture; both boards are consulted,
 * and a miss returns null so the leg stays honestly unknown. */
export interface EventFacts {
  sport_en?: string;
  league_en?: string;
  country_en?: string;
  is_live: boolean;
  starts_at?: string;
  /** English team names, when the board sent them — the favourites match keys on them. */
  home_en?: string;
  away_en?: string;
}

export interface BetRejection {
  stake_count: number;
  ours_leg_count: number;
  /** ⚠️ UNVERIFIED. Digitain's field name for a decline reason has NOT been
   * observed on a real board — see the device check in Task 10. We read a short
   * list of plausible names and send `null` when none is present, rather than
   * guessing a name and shipping a column that is silently always empty.
   * When the real field is captured, add it to `_REJECT_REASON_KEYS` and nothing
   * else has to change. */
  reason_code: string | null;
}

/** TOP-LEVEL candidate names for the decline reason.
 *
 * ⚠️ Every one of these is a guess and none has ever been seen live. They are
 * kept only as a fallback for an operator we have not met yet — the reason
 * Digitain actually sends lives in `StatusMessage`, see `_declineReason`.
 *
 * The value is length-capped because anything from this channel could be long
 * free text. */
const _REJECT_REASON_KEYS = ["ErrorCode", "ResultCode", "AlertMessageCode", "Code"];

/** Why the book declined the slip, or null if it did not say.
 *
 * ⛔ MEASURED 2026-08-27: `reason_code` was null on all 2,066 rejections the
 * pilot had recorded, because this only looked at `_REJECT_REASON_KEYS` — four
 * TOP-LEVEL names, all guessed. Digitain puts the status one level down, in
 * `StatusMessage`, which is the very object `handleMessage` already reads on the
 * AddStake path (where it captured a real value: "ERROR_STAKE_DOS_NOT_EXIST"
 * with Code 1304). The field was never missing; we were looking one level too
 * high.
 *
 * This matters beyond tidiness: a slip containing our legs is declined 38.5% of
 * the time against 26.0% for organic slips, and without a reason there is no way
 * to tell whether we are handing users dead stake ids, stale prices, or
 * something that was never our doing. */
function _declineReason(r: BettingResponse): string | null {
  const sm = r.StatusMessage;
  // Status first: it is human-readable and identifies the failure class.
  // Code second: present when Status is not, and still tells us WHICH failure.
  if (sm) {
    if (typeof sm.Status === "string" && sm.Status) return sm.Status.slice(0, 32);
    if (typeof sm.Code === "number") return String(sm.Code).slice(0, 32);
  }
  for (const k of _REJECT_REASON_KEYS) {
    const v = (r as unknown as Record<string, unknown>)[k];
    if (typeof v === "number" || (typeof v === "string" && v)) {
      return String(v).slice(0, 32);
    }
  }
  return null;
}

/** Matches the backend's `_DIM_VALUE_MAX_LEN`. Lowercase, non-alphanumerics → `_`. */
function slugify(v: string | undefined): string {
  const s = (v || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return (s || "unknown").slice(0, 32);
}

export interface BetPlacement {
  /** Identity of the BET, not of the event — reused across edits. Upsert on it. */
  order_number: number;
  bet_amount: number | null;
  bet_type: number | null;
  bet_type_name: string | null;
  stake_count: number;
  /** Π(accepted_odds) — computed, because BetFactor is always 0. Null when
   * suppressed (see `combined_odds_suppressed`). */
  combined_odds: number | null;
  /** True when a surfaced leg made the computed product misleading. The operator's
   * promo boost (`BF`, 1.15 observed) is NOT in the per-leg factors, so Π(factor)
   * would report 4.45 where the user actually received 5.12 — an understatement of
   * what the operator delivered, in a dashboard we show the operator. */
  combined_odds_suppressed: boolean;
  /** True when this receipt amends an existing order (see trap 2). */
  is_edit: boolean;
  /** Computed over `widget_add` legs ONLY, so its meaning is unchanged by the
   * surfaced signal: a purely promo-routed coupon is honestly `organic`. */
  attribution: AttributionClass;
  ours_leg_count: number;
  /** Legs we showed but did not insert. Reported SEPARATELY from `ours_leg_count`
   * and never folded into the attributed count — see `LegSource`. */
  surfaced_leg_count: number;
  /** Legs on a match we SENT the user to. Reported separately again: it is a
   * weaker claim than `surfaced` and must stay distinguishable in the dashboard,
   * or "we drove this" stops meaning one thing. */
  navigated_leg_count: number;
  /** Legs matching an outcome a card SHOWED this visitor, under an id we did not
   * insert — see `LegSource.shown`. Not part of `influenced`, which stays the
   * "we sent them to the match" route; the backend counts it as its own tier. */
  shown_leg_count: number;
  /** Any correlational route fired (`surfaced` or `navigated`). An upper bound on
   * our contribution, never a claim — `attribution` is the claim. */
  influenced: boolean;
  /** True when the coupon held a same-match Bet Builder item. Its legs are in
   * `legs`; `combined_odds` is the operator's builder price, not Π(leg factors). */
  bet_builder: boolean;
  legs: PlacedLeg[];
  seconds_since_add: number | null;
  seconds_since_surfaced: number | null;
  seconds_since_navigated: number | null;
  seconds_since_shown: number | null;
  /** Dims of the card behind the strongest claimed leg (inserted, else surfaced,
   * shown, navigated). ⛔ Was inserted legs ONLY, which left every navigated bet
   * with no `tier` and the tier table reading 0 bets under an arm with money. */
  dims?: EventDims;
  /** First attributed leg's identifiers — `event_id`/`rec_type` are first-class
   * DB columns on the events endpoint, so they travel outside the meta JSON. */
  event_id?: string;
  rec_type?: string;
  suggestion_id?: string;
}

/**
 * The `bet_placed` meta the backend reads off a placement.
 *
 * Lives here rather than inline in the integration so that adding a field to
 * `BetPlacement` and forgetting to SEND it is a test failure rather than a blank
 * column in the dashboard.
 */
export function placementEvent(p: BetPlacement): Record<string, unknown> {
  return {
    event_id: p.event_id,
    rec_type: p.rec_type,
    // `order_number` identifies the BET, not this event — an edited bet
    // re-reports under the SAME number. The backend must UPSERT on it:
    // dropping duplicates loses the edit, appending double-counts the stake.
    order_number: p.order_number,
    is_edit: p.is_edit,
    bet_amount: p.bet_amount,
    bet_type: p.bet_type,
    bet_type_name: p.bet_type_name,
    stake_count: p.stake_count,
    combined_odds: p.combined_odds,
    combined_odds_suppressed: p.combined_odds_suppressed,
    attribution: p.attribution,
    ours_leg_count: p.ours_leg_count,
    surfaced_leg_count: p.surfaced_leg_count,
    navigated_leg_count: p.navigated_leg_count,
    shown_leg_count: p.shown_leg_count,
    influenced: p.influenced,
    bet_builder: p.bet_builder,
    legs: p.legs,
    seconds_since_add: p.seconds_since_add,
    seconds_since_surfaced: p.seconds_since_surfaced,
    seconds_since_navigated: p.seconds_since_navigated,
    seconds_since_shown: p.seconds_since_shown,
    dims: p.dims,
    // Back-compat: the funnel reader still counts on the v1 boolean until the
    // backend learns `attribution`. Keep both until that lands. An INFLUENCED bet
    // is not attributed — `attribution` is computed over inserted legs only, so
    // this stays false for a promo-routed coupon, which is the point.
    attributed: p.attribution !== "organic",
  };
}

/** A stake we put in the slip, awaiting a placement. */
interface OurStake {
  suggestion_id: string;
  event_id?: string;
  rec_type?: string;
  rec_odds: number | null;
  selection?: string | null;
  dims: EventDims;
  ts: number;
}

/** Stakes we recommended are remembered this long. Generous: a user may build a
 * slip over several minutes, and an edit can revisit a bet much later. Entries
 * are cheap (one small object per add) and pruned on every placement. */
const OURS_TTL_MS = 60 * 60_000;

/** Stakes we merely SHOWED expire in half that time. The claim is correlational,
 * and the wider the window the likelier the real cause is Digitain's own promo
 * card — which sits above the fold on their Главная tab showing the same picks. */
const SURFACED_TTL_MS = 30 * 60_000;

/** Matches we SENT the user to count for the VISIT, not a clock. OWNER 2026-09-15:
 * "every football match is like 2.5 hours. Until the user leaves the page, we can
 * count." The old 30 minutes dropped the late coupons of exactly the match we
 * opened — measured 7d: 11 bets worth 7,825 between minute 60 and 180 alone.
 * The registry now lives in sessionStorage (`persistTo`), so it dies with the tab;
 * this is only the safety prune for a tab left open overnight.
 * ⛔ Still ONE visitor: the registry is this browser's own. */
const NAVIGATED_TTL_MS = 12 * 60 * 60_000;

/** Outcomes a card SHOWED this visitor — same visit rule as `navigated`. */
const SHOWN_TTL_MS = NAVIGATED_TTL_MS;

/** The identity of an OUTCOME that survives a stake-id rotation. A missing line
 * (a moneyline) and a null argument are the same fact, so both key as "". */
const outcomeKey = (eventId: unknown, marketId: unknown, code: unknown, line: unknown, part: string | null | undefined): string | null => {
  if (eventId == null || eventId === "" || marketId == null || code == null) return null;
  return `${eventId}|${marketId}|${code}|${line == null ? "" : Number(line)}${partSuffix(part)}`;
};

/** Minimal storage surface, so a test can hand in a Map-backed fake. */
export interface RegistryStorage {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
}

/** The card's descriptive dims PLUS its risk tier. `suggestionDims` alone drops
 * `tier` (only `outcomeDims` adds it), so every registry filed a card without one
 * and a bet it led to could never land in `by_dimension.tier`. */
const cardDims = (s: Suggestion, rank: number): EventDims =>
  ({ ...suggestionDims(s, rank), ...(s.tier ? { tier: s.tier } : {}) });

const isBettingPath = (url: string): boolean => url.indexOf("/Betting/") >= 0;
/** Matches both /Betting/PlaceBet and /Betting/PlaceBetPlus (trap 1 in §5.2 of
 * the design doc — an exact-path match silently drops every edited bet). */
const isPlacementPath = (url: string): boolean => url.indexOf("PlaceBet") >= 0;

interface BettingResponse {
  StatusMessage?: { Status?: string; Code?: number } | null;
  CheckedStakesList?: Array<{
    StakeId?: number;
    Factor?: number;
    Deleted?: boolean;
    // Identity of the OUTCOME behind the stake, so a dead leg can be re-resolved
    // to the id the board is using now. All four are present on every coupon leg
    // (verified on the Parikara wire 2026-08-13).
    EventId?: number;
    ParentEventId?: number;
    EventPartTypeId?: number;
    PeriodId?: number;
    StakeTypeId?: number;
    StakeCode?: number;
    Argument?: number | null;
    // A same-match Bet Builder is ONE coupon item with `StakeId: null` and its
    // legs here, each shaped like a coupon leg (StakeId, EventId, StakeTypeId…).
    BetBuilderStakes?: Array<{
      StakeId?: number;
      Factor?: number;
      EventId?: number;
      ParentEventId?: number;
      EventPartTypeId?: number;
      PeriodId?: number;
      StakeTypeId?: number;
      StakeCode?: number;
      Argument?: number | null;
    }> | null;
  }> | null;
  IsBetPlaced?: boolean;
  IsBetEdit?: boolean;
  OrderNumber?: number;
  BetAmount?: number;
  BetType?: number;
  BetTypeName?: string;
  StakeCount?: number;
}

/** A coupon leg the operator accepted and then flagged `Deleted`, carried with the
 * identity that outlives a stake-id rotation. */
export interface DeadLeg {
  stakeId: number;
  eventId: number;
  marketId: number;
  code: number;
  argument: number | null;
}

export class BettingBusWatcher {
  /** Stakes WE put in the slip: stakeId → the suggestion behind it. */
  private ours = new Map<number, OurStake>();
  /** Stakes we SHOWED but did not insert, because the tap was routed to the
   * operator's own promo card to preserve their boost. Kept in a separate map, not
   * a flag on `ours`, so no future edit can accidentally sum the two. */
  private surfaced = new Map<number, OurStake>();
  /** Server-authoritative slip, rebuilt from CheckedStakesList on every message —
   * never inferred from our own posts, so a hand-edited slip is reflected too. */
  private slip: Array<{
    stakeId: number; factor: number | null; eventId: number | null; fixtureId: number | null; part: string;
    marketId: number | null; stakeCode: number | null; argument: number | null;
    /** The Bet Builder item's own price when this leg rode inside one, else null. */
    bbFactor: number | null;
  }> = [];
  /** First observed time for each stake currently in the server-authoritative slip. */
  private slipFirstSeen = new Map<number, number>();
  /** Owner 2026-10-12: a native tap before coupon entry is the only honest join. */
  private slipEntry = new Map<number, LegEntry>();
  private taps = new Map<string, TapEntry>();
  /** Stakes already present when this watcher first received a slip snapshot. */
  private slipAgeFloors = new Set<number>();
  private seenSlipSnapshot = false;
  /** Matches we SENT the user to, keyed by EVENT id — see `LegSource.navigated`.
   * Keyed on the event because the stake they end up taking is the thing we
   * deliberately do not control. */
  private navigated = new Map<number, OurStake>();
  /** Outcomes a card SHOWED, keyed by `outcomeKey` — see `LegSource.shown`. */
  private shown = new Map<string, OurStake>();
  /** Where the four registries persist across the SPA's SDK remounts; null = memory only. */
  private registryStore: { storage: RegistryStorage; key: string } | null = null;
  /** Legs the operator accepted into the coupon but flagged `Deleted` — present on
   * screen, greyed, and impossible to place. Rebuilt from the same list as `slip`. */
  private dead: DeadLeg[] = [];
  /** Opened by GetCouponForUpdate(IsBetEdit:true), closed by the next receipt. */
  private editSession = false;
  private listener: ((e: MessageEvent) => void) | null = null;
  private seenBus = false;
  /**
   * The app's OWN coupon, read from its redux store — a SECOND source beside the
   * bus messages, wired by the integration (`coupon-store.ts`), and `null` when
   * this operator has no readable store (iframe operators, a reshaped app).
   *
   * ⛔ It is not a substitute for the bus: the verdict still prefers an operator
   * answer. It exists because a silent channel is not evidence about the coupon —
   * measured 2026-09-12, when every add for ~5 hours read `landed=0` off a bus that
   * had simply stopped carrying coupon messages while the app worked normally.
   */
  storeCoupon: () => number[] | null = () => null;
  /** Counts every /Betting/ message — confirmAdd uses it to tell "the operator
   * answered no" apart from "this operator never answers". */
  private busMessages = 0;
  private waiters = new Set<(ok: boolean) => void>();
  private confirming = new Set<(ok: boolean) => void>();
  private nativeTapDuringConfirmation = false;
  /** Native coupon changes can arrive without an http-api-response relay. */
  subscribeCoupon: (notify: () => void) => () => void = () => () => {};
  private couponUnsubscribers = new Set<() => void>();
  private lastNativeCoupon: number[] | null = null;
  private nativeRemoved = new Set<number>();

  private observeCoupon(notify: () => void): () => void {
    const unsubscribe = this.subscribeCoupon(notify);
    let active = true;
    const stop = (): void => {
      if (!active) return;
      active = false;
      this.couponUnsubscribers.delete(stop);
      unsubscribe();
    };
    this.couponUnsubscribers.add(stop);
    return stop;
  }
  /** How the most recent confirmAdd resolved — see lastAddResolution(). */
  private resolution: AckResolution = { how: "immediate", status: null };
  /** The operator's status string from the decline that woke the waiters. */
  private lastDeclineStatus: string | null = null;
  /**
   * Fired when a CONFIRMED leg of ours leaves the coupon without a placement —
   * the user dropped the pick (or emptied the slip) rather than betting it.
   * `heldMs` is how long they held it. Three disappearances that are NOT this
   * are suppressed: a placement (the goal), a removal we posted ourselves (the
   * sweep — announce it via expectRemoval first), and a leg the operator
   * flagged Deleted (that is the book refusing, not the user deciding).
   */
  onOurLegRemoved?: (r: { stakeId: number; eventId: number | null; heldMs: number }) => void;
  /** Ids whose removal WE are about to cause (retract/removeDead). Consumed. */
  private expectedRemovals = new Set<number>();
  /** Ids consumed by a placement receipt — their disappearance is success. */
  private placedIds = new Set<number>();
  /** Removals already reported, so a stream of coupon reads fires once. */
  private removalReported = new Set<number>();
  private subEventPart?: (eventId: number) => string | null;

  setSubEventParts(lookup: (eventId: number) => string | null): void {
    this.subEventPart = lookup;
  }

  /** Call BEFORE posting a retraction, so the resulting coupon change is not
   *  reported as the user dropping the pick. */
  expectRemoval(ids: number[]): void {
    for (const id of ids) if (Number.isFinite(id)) this.expectedRemovals.add(Number(id));
  }

  constructor(
    private onPlaced: (p: BetPlacement) => void,
    /** Fired once, the first time any /Betting/ traffic is seen. The integration
     * uses it to retire the legacy successBet watcher for this session. */
    private onBusDetected?: () => void,
    /** The board we already hold, keyed by stake id — describes a leg the user
     * picked themselves (no `source`) without a single extra request. Absent →
     * those legs stay unreadable, same as before this parameter existed. */
    private lookupStake?: (stakeId: number) => StakeFacts | null,
    /** Fired when the operator answers a placement with `IsBetPlaced: false` —
     * previously dropped on the floor at the early return below. */
    private onRejected?: (r: BetRejection) => void,
    /** BOTH boards, keyed by EVENT id — the live catalogue and the pre-match
     * board. This is what lets a leg say WHEN it was placed: live, or on a
     * fixture that had not started, with its kick-off instant.
     *
     * ⛔ Separate from `lookupStake`, not a replacement for it: that one names
     * the OUTCOME («П1»), which only a stake id can identify. This one names the
     * EVENT, and survives the stake-id rotation that makes the other miss.
     *
     * Absent, or a miss → the leg records its event id and claims nothing else. */
    private lookupEvent?: (eventId: number) => EventFacts | null,
  ) {}

  /** Call when a suggestion's stakes actually went into the operator's betslip. */
  recordAdd(s: Suggestion, rank = 0, successors?: ReadonlyMap<number, number>): void {
    this.register(this.ours, s, rank, successors);
  }

  /** The visitor's ★ favourites (favourites.ts), for `fav` on each placed leg. */
  private favouritesOf: (() => FavouritesSnapshot | null) | null = null;

  setFavourites(fn: () => FavouritesSnapshot | null): void {
    this.favouritesOf = fn;
  }

  private favOf(fixtureId: number | null, entry: LegEntry | undefined): { fav?: FavouriteKind } {
    try {
      const snap = this.favouritesOf?.() ?? null;
      if (!snap) return {};
      const ev = fixtureId != null ? this.lookupEvent?.(fixtureId) ?? null : null;
      return { fav: favouriteKind(snap, { event_id: fixtureId, home_en: ev?.home_en,
                                          away_en: ev?.away_en, league_en: ev?.league_en,
                                          champ_id: entry?.champ_id }) };
    } catch { return {}; }
  }

  noteTap(tap: TapEntry): void {
    if (this.confirming.size) this.nativeTapDuringConfirmation = true;
    const keys = [
      ...(tap.stake_id != null ? [`s:${tap.stake_id}`] : []),
      // A promo card names its own legs: an exact join for each of them.
      ...(tap.card_stake_ids ?? []).map((id) => `s:${id}`),
      ...(tap.stake_id == null && tap.event_id != null ? [`e:${tap.event_id}`] : []),
    ];
    for (const key of keys) {
      this.taps.delete(key);
      this.taps.set(key, tap);
    }
    while (this.taps.size > 100) this.taps.delete(this.taps.keys().next().value as string);
  }

  /**
   * Call when the user TAPS a card whose stakes we showed but did not insert —
   * the SUPER PROMOTIONS route, where we send the tap to Digitain's own promo card
   * so the user keeps the operator's boost (`BF`, 1.15 observed; a rebuild via
   * `toggleStake` measured 4.44 delivered against 5.12 advertised).
   *
   * **This call site IS the interaction gate.** An impression must never reach
   * here. Gating at classification time instead would put the check somewhere a
   * later edit can quietly drop; gating at the only entrance to the map cannot be
   * undone by accident.
   */
  recordSurfaced(s: Suggestion, rank = 0): void {
    this.register(this.surfaced, s, rank);
  }

  /**
   * Call when we actually took the user TO a match — the navigate action, or
   * re-opening a saved one. `eventId` is the match we landed on, not the stake we
   * liked, because from here the user chooses their own market.
   *
   * Like `recordSurfaced`, this call site IS the gate: only a real navigation may
   * reach it. An impression, a render, or a navigation we merely ATTEMPTED must
   * not — `DigitainSink.add` returns null when nothing moved, and that null is
   * what keeps this honest. Registering a navigation that did not happen would
   * quietly convert the user's own browsing into our influence.
   */
  recordNavigate(s: Suggestion, eventId: number, rank = 0): void {
    if (!Number.isFinite(eventId) || eventId <= 0) return;
    const dims = cardDims(s, rank);
    this.navigated.set(Number(eventId), {
      suggestion_id: s.id,
      event_id: String(eventId),
      rec_type: s.rec_type,
      rec_odds: typeof s.odds === "number" ? s.odds : null,
      selection: s.selection ?? null,
      dims,
      ts: Date.now(),
    });
    this.save();
  }

  /** Call where an IMPRESSION is sent: remember each outcome the card showed, by
   * the identity that survives a stake-id rotation. An outcome with no market id
   * or code cannot be matched honestly and is not filed. */
  recordShown(s: Suggestion, rank = 0): void {
    const dims = cardDims(s, rank);
    const now = Date.now();
    const outs = s.legs && s.legs.length
      ? s.legs.map((l) => ({ key: outcomeKey(l.event_id, l.market_id, l.code, l.line, l.part), l }))
          .map(({ key, l }) => ({ key, eventId: l.event_id, odds: l.odds ?? null, selection: l.selection ?? null }))
      : [{ key: outcomeKey(s.event_id, s.market_id, s.code, s.line, s.part), eventId: s.event_id,
           odds: s.odds ?? null, selection: s.selection ?? null }];
    let any = false;
    for (const o of outs) {
      if (!o.key) continue;
      any = true;
      this.shown.set(o.key, {
        suggestion_id: s.id, event_id: o.eventId, rec_type: s.rec_type,
        rec_odds: typeof o.odds === "number" ? o.odds : null,
        selection: o.selection, dims, ts: now,
      });
    }
    if (any) this.save();
  }

  /** Persist the registries across the SPA's SDK remounts and reloads, for the
   * life of the tab. Loads whatever this tab already holds, then writes on change. */
  persistTo(storage: RegistryStorage, key: string): void {
    this.registryStore = { storage, key };
    try {
      const raw = storage.getItem(key);
      if (!raw) return;
      const o = JSON.parse(raw) as Record<string, Array<[string | number, OurStake]>>;
      const fill = <K>(into: Map<K, OurStake>, rows: unknown, num: boolean) => {
        if (!Array.isArray(rows)) return;
        for (const r of rows) {
          if (!Array.isArray(r) || r.length !== 2 || !r[1] || typeof r[1].ts !== "number") continue;
          if (!into.has((num ? Number(r[0]) : String(r[0])) as K)) {
            into.set((num ? Number(r[0]) : String(r[0])) as K, r[1] as OurStake);
          }
        }
      };
      fill(this.ours, o.ours, true);
      fill(this.surfaced, o.surfaced, true);
      fill(this.navigated, o.navigated, true);
      fill(this.shown, o.shown, false);
      if (Array.isArray(o.slipFirstSeen)) {
        for (const row of o.slipFirstSeen) {
          if (!Array.isArray(row) || row.length !== 2) continue;
          const id = Number(row[0]);
          const ts = Number(row[1]);
          if (Number.isFinite(id) && id > 0 && Number.isFinite(ts)) this.slipFirstSeen.set(id, ts);
        }
      }
      if (Array.isArray(o.slipAgeFloors)) {
        for (const rawId of o.slipAgeFloors) {
          const id = Number(rawId);
          if (this.slipFirstSeen.has(id)) this.slipAgeFloors.add(id);
        }
      }
      if (Array.isArray(o.slipEntry)) {
        for (const row of o.slipEntry) {
          if (!Array.isArray(row) || row.length !== 2) continue;
          const id = Number(row[0]);
          if (Number.isFinite(id) && id > 0 && row[1] && typeof row[1] === "object") {
            this.slipEntry.set(id, row[1] as unknown as LegEntry);
          }
        }
      }
      this.prune(Date.now());
    } catch { /* unreadable storage: memory only, as before */ }
  }

  private save(): void {
    if (!this.registryStore) return;
    try {
      this.registryStore.storage.setItem(this.registryStore.key, JSON.stringify({
        ours: [...this.ours], surfaced: [...this.surfaced],
        navigated: [...this.navigated], shown: [...this.shown],
        slipFirstSeen: [...this.slipFirstSeen], slipAgeFloors: [...this.slipAgeFloors],
        slipEntry: [...this.slipEntry],
      }));
    } catch { /* quota / private mode: memory only */ }
  }

  /** Expand a suggestion into its stakes and file them under one registry. */
  private register(into: Map<number, OurStake>, s: Suggestion, rank: number,
                   successors?: ReadonlyMap<number, number>): void {
    const dims = cardDims(s, rank);
    const now = Date.now();
    const legs = s.legs && s.legs.length
      ? s.legs.map((l) => ({
          stakeId: Number(l.stake_id), odds: l.odds ?? null,
          eventId: l.event_id, selection: l.selection ?? l.match ?? null,
        }))
      : [{ stakeId: Number(s.stake_id), odds: s.odds ?? null, eventId: s.event_id, selection: s.selection ?? null }];
    for (const leg of legs) {
      if (!Number.isFinite(leg.stakeId) || leg.stakeId <= 0) continue;
      into.set(successors?.get(leg.stakeId) ?? leg.stakeId, {
        suggestion_id: s.id,
        event_id: leg.eventId,
        rec_type: s.rec_type,
        rec_odds: typeof leg.odds === "number" ? leg.odds : null,
        selection: leg.selection ?? null,
        dims,
        ts: now,
      });
    }
    this.prune(now);
  }

  /** True once any /Betting/ message has been seen — the bus exists here. */
  detected(): boolean {
    return this.seenBus;
  }

  /** The price the operator ACCEPTED for a stake now in the slip, if known.
   * Digitain's stake ids rotate as odds move, so what we displayed and what the
   * slip took can differ (measured: recommended 1.68 → accepted 1.66). */
  /** How many legs the operator's coupon holds right now (ours and theirs). */
  slipSize(): number {
    return this.slip.length;
  }

  acceptedOdds(stakeId: number): number | null {
    const leg = this.slip.find((s) => s.stakeId === stakeId);
    return leg ? leg.factor : null;
  }

  /**
   * Wait for the operator's own answer to an add we just posted.
   *
   * This replaces the BookAbet mint probe: instead of minting a booking number to
   * infer that stakes are still live, we read `/Betting/AddStake` — the operator
   * telling us directly whether the leg landed, and at what price. One fewer
   * round-trip, and direct evidence instead of a proxy for it.
   *
   * Resolves `no-bus` when NOTHING answered, which is how an operator without the
   * betting bus (Hannibalbet's cross-origin iframe) is distinguished from one that
   * answered "no" — the two must not be conflated, or a working add gets reported
   * as a failure.
   */
  confirmAdd(stakeIds: number[], timeoutMs = 2500,
             onResolution?: (r: AckResolution) => void): Promise<AddVerdict> {
    const wanted = stakeIds.filter((n) => Number.isFinite(n) && n > 0);
    if (wanted.length === 0) {
      this.resolution = { how: "immediate", status: null };
      onResolution?.(this.resolution);
      return Promise.resolve("rejected");
    }
    if (this.hasAll(wanted)) {
      this.resolution = { how: "immediate", status: null };
      onResolution?.(this.resolution);
      return Promise.resolve("confirmed");
    }

    return new Promise<AddVerdict>((resolve) => {
      if (this.confirming.size === 0) this.nativeTapDuringConfirmation = false;
      let done = false;
      let stopCoupon = (): void => {};
      const finish = (v: AddVerdict, how: AckResolution["how"]): void => {
        if (done) return;
        done = true;
        this.waiters.delete(waiter);
        this.confirming.delete(waiter);
        clearTimeout(timer);
        stopCoupon();
        // The status string only belongs to an explicit decline; a timeout has
        // no operator answer to quote, and quoting a stale one would fabricate.
        this.resolution = { how, status: how === "declined" ? this.lastDeclineStatus : null };
        onResolution?.(this.resolution);
        resolve(v);
      };
      const waiter = (ok: boolean): void => {
        if (this.hasAll(wanted)) finish("confirmed", "ack");
        else if (!ok) finish(this.classify(wanted), "declined"); // an explicit error status came back
      };
      const timer = setTimeout(() => {
        // Silence means "no ack channel here", not "declined" — but ONLY when
        // this operator has never spoken at all. An operator whose bus we HAVE
        // seen (Parikara) and which then ignores our add has not answered "no
        // channel"; falling through to Hannibalbet's mint there would be wrong.
        // ⛔ BEFORE calling it a timeout, read the app's own coupon. A silent bus is
        // a statement about the CHANNEL; the store is a statement about the SLIP, and
        // 2026-09-12 showed the two disagreeing for hours — the card told users
        // «не удалось добавить» while the app's coupon held their legs.
        if (this.hasAll(wanted)) return finish("confirmed", "store");
        if (!this.seenBus) return finish("no-bus", "no-bus");
        finish(this.classify(wanted), "timeout");
      }, timeoutMs);
      this.waiters.add(waiter);
      this.confirming.add(waiter);
      stopCoupon = this.observeCoupon(() => {
        if (this.hasAll(wanted)) finish("confirmed", "store");
      });
      // A subscription may report its initial state synchronously.
      if (done) stopCoupon();
    });
  }

  /**
   * Late-ack settlement. Parikara's traffic is mobile Turkmenistan, so an
   * AddStake ack can arrive well after any verdict window a user would tolerate.
   * This watches the coupon for `ids` after a timeout verdict: it settles EARLY
   * the moment every id is present, or at `windowMs` with whatever subset
   * landed — so the caller can correct a timeout verdict (flip the card, confirm
   * the sink, report the real latency) instead of letting it stand wrong.
   * The callback fires exactly once.
   */
  watchLanded(
    ids: number[],
    windowMs: number,
    onSettle: (s: { landed: number[]; allLanded: boolean; elapsedMs: number }) => void,
    successorOf: (id: number) => number = (id) => id,
  ): void {
    const wanted = ids.filter((n) => Number.isFinite(n) && n > 0);
    if (wanted.length === 0) return;
    const t0 = Date.now();
    let done = false;
    let stopCoupon = (): void => {};
    const settle = (): void => {
      if (done) return;
      done = true;
      this.waiters.delete(waiter);
      clearTimeout(timer);
      stopCoupon();
      const landed = this.landedStakes(wanted.map(successorOf));
      onSettle({ landed, allLanded: landed.length === wanted.length, elapsedMs: Date.now() - t0 });
    };
    const waiter = (_ok: boolean): void => {
      if (this.landedStakes(wanted.map(successorOf)).length === wanted.length) settle();
    };
    const timer = setTimeout(settle, windowMs);
    this.waiters.add(waiter);
    stopCoupon = this.observeCoupon(() => waiter(true));
    if (done) stopCoupon();
    // Catch a landing between confirmAdd's timeout and registering this watch.
    waiter(true);
  }

  /**
   * HOW the last confirmAdd resolved — the distinction §"the add does not land"
   * needed and the verdict alone cannot carry: `rejected` used to mean both
   * "the operator answered no" (`declined`, with the operator's own status
   * string, e.g. ERROR_STAKE_DOS_NOT_EXIST) and "nobody answered within the
   * window" (`timeout`). Read it right after awaiting confirmAdd; a later call
   * overwrites it.
   */
  lastAddResolution(): AckResolution {
    return this.resolution;
  }

  private hasAll(ids: number[]): boolean {
    return this.landedStakes(ids).length === ids.length;
  }

  /** How much of what we asked for is actually in the coupon. `partial` exists
   *  because a 2-of-3 combo is not a refusal — the user is holding two legs. */
  private classify(wanted: number[]): AddVerdict {
    const landed = this.landedStakes(wanted).length;
    if (landed === 0) return "rejected";
    return landed === wanted.length ? "confirmed" : "partial";
  }

  /**
   * The subset of `ids` the OPERATOR'S coupon actually contains.
   *
   * This is the only honest answer to "did our add land?". `DigitainSink`'s own
   * bookkeeping cannot answer it — that set is written at POST time, before any
   * confirmation. Anything that re-posts `toggleStake` (which ADDS a leg when it
   * is absent) must ask HERE first, or it writes bets the user never chose.
   */
  /**
   * The operator's whole coupon, as stake ids — or `null` where this operator has
   * never spoken on the bus, because there an empty coupon and no channel at all
   * are the same observation and must not be read as "the user holds nothing".
   */
  couponStakeIds(): number[] | null {
    const fromBus = this.seenBus
      ? this.slip.filter(s => !this.nativeRemoved.has(s.stakeId)).map(s => s.stakeId) : null;
    const fromStore = this.storeCoupon();
    if (!fromStore) return fromBus;
    if (!fromBus) return fromStore;
    // Union: the store carries legs the user added by hand that a stale bus read
    // would miss, and the bus carries a leg the store has not re-rendered yet.
    return Array.from(new Set([...fromBus, ...fromStore]));
  }

  /** Dead legs the operator's coupon is currently holding, restricted to ids the
   * caller actually posted. Scoping to `ids` matters: a leg the USER added by hand
   * and let go dead is theirs, and silently rewriting it would be the overlay
   * editing a coupon nobody asked it to touch. */
  deadStakes(ids: number[]): DeadLeg[] {
    const wanted = new Set(ids);
    return this.dead.filter((d) => wanted.has(d.stakeId));
  }

  landedStakes(ids: number[]): number[] {
    const inSlip = new Set(this.slip.filter(s => !this.nativeRemoved.has(s.stakeId)).map(s => s.stakeId));
    // ⛔ The app's own store is read HERE, not only on a message. Measured
    // 2026-09-12: the bus went silent for every add while the app kept working, so
    // a message-only reading said `landed=0` about legs the app had in its coupon.
    const fromStore = this.storeCoupon();
    if (fromStore) for (const id of fromStore) inSlip.add(id);
    return ids.filter((id) => inSlip.has(id));
  }

  start(): void {
    if (this.listener) return;
    this.lastNativeCoupon = this.storeCoupon();
    this.observeCoupon(() => {
      const current = this.storeCoupon();
      if (current == null) return; // unreadable is never an empty coupon
      const present = new Set(current);
      const removed = new Set((this.lastNativeCoupon ?? []).filter(id => !present.has(id)));
      // Retain newly acknowledged bus legs while native rendering catches up,
      // but not a leg the native coupon actually held and then removed quietly.
      for (const id of removed) {
        if (this.slip.some(leg => leg.stakeId === id)) this.nativeRemoved.add(id);
      }
      for (const id of present) this.nativeRemoved.delete(id);
      this.lastNativeCoupon = current;
    });
    this.listener = (e: MessageEvent): void => {
      const d = e.data as { sender?: string; url?: unknown; res?: unknown } | null;
      if (!d || typeof d !== "object" || d.sender !== "http-api-response") return;
      if (typeof d.url !== "string") return;
      this.handleMessage(d.url, d.res);
    };
    try {
      window.addEventListener("message", this.listener);
    } catch {
      this.listener = null; // no window (tests drive handleMessage directly)
    }
  }

  stop(): void {
    for (const stop of [...this.couponUnsubscribers]) stop();
    this.lastNativeCoupon = null;
    if (!this.listener) return;
    try {
      window.removeEventListener("message", this.listener);
    } catch {
      /* ignore */
    }
    this.listener = null;
  }

  /** Exposed for tests; start() routes real relay messages here. */
  handleMessage(url: string, res: unknown): void {
    if (!isBettingPath(url)) return;
    this.busMessages += 1;
    if (!this.seenBus) {
      this.seenBus = true;
      this.onBusDetected?.();
    }
    const r = (res && typeof res === "object" ? res : {}) as BettingResponse;

    // The coupon as it stood BEFORE this message — the removal diff's baseline.
    const prevSlipIds = this.slip.map((s) => s.stakeId);

    // The slip is whatever the server says it is, on EVERY betting message.
    if (Array.isArray(r.CheckedStakesList)) {
      this.nativeRemoved.clear(); // a new server snapshot supersedes the old bus reading
      this.slip = r.CheckedStakesList
        // `Deleted: true` is IN the list but is NOT a bettable leg. Digitain
        // accepts a stake id that has vanished from the board — Success status,
        // the leg present here — then greys the coupon row ("Доступность событий
        // изменилась") and swaps the place button for a "Принять" that cannot fix
        // it, because the stake is simply not available any more. Reproduced live
        // on Parikara 2026-08-13 by posting an id observed leaving the feed 35s
        // earlier. Counting it as landed made `landedStakes` say yes, `resolveAdd`
        // say added, and the card flip to "✓ In betslip" over a dead leg the user
        // then has to delete by hand.
        .filter((s) => s?.Deleted !== true)
        // ⛔ A Bet Builder item has `StakeId: null` and its legs in
        // `BetBuilderStakes[]`. Read at the top level only, it failed the
        // `stakeId > 0` filter below and a 3-leg builder bet was stored with
        // `legs: []` — no event, no markets, and `organic` even when we added it
        // (order 7668041941, 2026-09-14). Each builder leg becomes a slip leg.
        .flatMap((s) => {
          const inner = Array.isArray(s?.BetBuilderStakes) ? s.BetBuilderStakes : [];
          if (!inner.length) return [{ ...s, bbFactor: null as number | null }];
          const bbFactor = typeof s?.Factor === "number" ? s.Factor : null;
          return inner.map((l) => ({ ...l, bbFactor }));
        })
        .map((s) => ({
          bbFactor: s.bbFactor,
          stakeId: Number(s?.StakeId),
          factor: typeof s?.Factor === "number" ? s.Factor : null,
          // Kept so a leg can be matched against a match we navigated to. The
          // stake id cannot do that job: the user picks their own outcome.
          eventId: Number.isFinite(Number(s?.EventId)) ? Number(s?.EventId) : null,
          fixtureId: Number.isFinite(Number(s?.ParentEventId)) && Number(s?.ParentEventId) > 0
            ? Number(s.ParentEventId)
            : (Number.isFinite(Number(s?.EventId)) ? Number(s.EventId) : null),
          // A leg that names its part type but no period is in period 0, not "NaN".
          // A board lookup refines that zero when it knows the sub-event's part.
          // ⛔ `betslip-sub-event-single-41153975.json` has a sub-event single
          // with EventPartTypeId but no PeriodId.
          part: Number.isFinite(Number(s?.EventPartTypeId)) && Number(s?.EventPartTypeId) > 0
            ? (s?.PeriodId != null && Number.isFinite(Number(s.PeriodId))
              ? `${Number(s.EventPartTypeId)}:${Number(s.PeriodId)}`
              : (Number.isFinite(Number(s?.EventId))
                ? this.subEventPart?.(Number(s.EventId)) || `${Number(s.EventPartTypeId)}:0`
                : `${Number(s.EventPartTypeId)}:0`))
            : "",
          // WHICH MARKET. Read here rather than in the placement path because
          // this is the one place the server's own view of the slip arrives;
          // the receipt reports the same legs and the dead-leg branch below has
          // been reading exactly these three fields since 2026-08-13.
          marketId: Number.isFinite(Number(s?.StakeTypeId)) ? Number(s?.StakeTypeId) : null,
          stakeCode: Number.isFinite(Number(s?.StakeCode)) ? Number(s?.StakeCode) : null,
          // ⚠️ `Argument` is legitimately null on a market with no line, and
          // that is DIFFERENT from a field we could not read. Only a finite
          // number or an explicit null survives.
          argument: s?.Argument == null ? null
            : (Number.isFinite(Number(s.Argument)) ? Number(s.Argument) : null),
        }))
        .filter((s) => Number.isFinite(s.stakeId) && s.stakeId > 0);

      // The other half of the same list: legs the operator ACCEPTED but flagged
      // dead. They sit in the user's coupon, greyed and unplaceable, until
      // something removes them — so they are kept (with the identity needed to
      // re-resolve the outcome) rather than merely filtered away.
      this.dead = r.CheckedStakesList
        .filter((s) => s?.Deleted === true && Number(s?.StakeId) > 0)
        .map((s) => ({
          stakeId: Number(s.StakeId),
          eventId: Number(s.EventId),
          marketId: Number(s.StakeTypeId),
          code: Number(s.StakeCode),
          argument: s.Argument == null ? null : Number(s.Argument),
        }))
        .filter((d) => Number.isFinite(d.eventId) && Number.isFinite(d.marketId) && Number.isFinite(d.code));

      const firstSnapshot = !this.seenSlipSnapshot;
      const now = Date.now();
      const currentIds = new Set(this.slip.map((s) => s.stakeId));
      // ⛔ A tap is spent by the slip update it produced. Kept, an event-keyed promo
      // tap would stamp a leg the visitor added on that event hours later from the
      // match page. All new legs of ONE update share it (a Betbuilder card lands
      // several legs at once), then it is deleted.
      const spent = new Set<TapEntry>();
      for (const id of currentIds) {
        if (this.slipFirstSeen.has(id)) continue;
        this.slipFirstSeen.set(id, now);
        const leg = this.slip.find((s) => s.stakeId === id);
        // A sub-event leg (corners, cards) names its own event id and the parent
        // fixture separately; a promo card may carry either.
        const key = this.taps.has(`s:${id}`) ? `s:${id}`
          : [leg?.eventId, leg?.fixtureId].map((e) => (e == null ? null : `e:${e}`))
            .find((k): k is string => k != null && this.taps.has(k)) ?? null;
        const tap = key ? this.taps.get(key) : undefined;
        if (tap && key) {
          const { stake_id: _stakeId, event_id: _eventId, card_stake_ids: _card, ...entry } = tap;
          this.slipEntry.set(id, entry);
          spent.add(tap);
        }
        if (firstSnapshot) this.slipAgeFloors.add(id);
      }
      // every key of a spent tap goes: a card tap is filed under each of its legs
      for (const [k, t] of [...this.taps]) if (spent.has(t)) this.taps.delete(k);
      for (const id of this.slipFirstSeen.keys()) {
        if (!currentIds.has(id)) {
          this.slipFirstSeen.delete(id);
          this.slipAgeFloors.delete(id);
          this.slipEntry.delete(id);
        }
      }
      this.seenSlipSnapshot = true;
      this.save();

      // ── Did one of OUR confirmed legs just leave the coupon? ────────────
      const nowIds = new Set(this.slip.map((s) => s.stakeId));
      const deadIds = new Set(this.dead.map((d) => d.stakeId));
      for (const id of prevSlipIds) {
        if (nowIds.has(id)) {
          this.removalReported.delete(id); // re-landed — a future drop counts again
          continue;
        }
        const ourLeg = this.ours.get(id);
        if (!ourLeg || this.removalReported.has(id)) continue;
        if (this.placedIds.delete(id)) continue;        // consumed by a receipt
        if (this.expectedRemovals.delete(id)) continue; // our own sweep
        if (deadIds.has(id)) continue;                  // the book killed it, not the user
        this.removalReported.add(id);
        this.onOurLegRemoved?.({
          stakeId: id,
          eventId: ourLeg.event_id != null && Number.isFinite(Number(ourLeg.event_id))
            ? Number(ourLeg.event_id) : null,
          heldMs: Date.now() - ourLeg.ts,
        });
      }
    }

    // An edit session opens here and stays open until the receipt — the receipt
    // itself reports IsBetEdit:false, so this flag is the only reliable marker.
    if (url.indexOf("GetCouponForUpdate") >= 0 && r.IsBetEdit === true) {
      this.editSession = true;
    }

    // Wake anything awaiting an add ack. A non-Success status on AddStake is the
    // operator declining outright (measured: "ERROR_STAKE_DOS_NOT_EXIST"/1304 when
    // a stake id has rotated); anything else just re-checks the slip.
    if (this.waiters.size) {
      const status = r.StatusMessage?.Status;
      const declined = url.indexOf("AddStake") >= 0 && typeof status === "string" && status !== "Success";
      if (declined) this.lastDeclineStatus = status as string;
      // This envelope has no rejected-stake/request identity. With concurrent
      // adds, broadcasting its error would reject unrelated pending coupons.
      // Complete wanted coupons still confirm; the others keep their deadline.
      const singlePendingDecline = declined && this.confirming.size === 1
        && !this.nativeTapDuringConfirmation;
      for (const waiter of [...this.waiters]) waiter(!singlePendingDecline);
    }

    if (!isPlacementPath(url)) return;
    // `IsBetPlaced === false` specifically, not merely "not true" — a URL only
    // has to CONTAIN "PlaceBet" to match `isPlacementPath`, so a pre-check, a
    // status poll, an `-Info`/`-Check` sibling, or an error envelope on that
    // same path can all lack the field entirely without being a decline. Only
    // a response that explicitly says `IsBetPlaced: false` is a rejection;
    // anything else (including `undefined`) falls through and is ignored here.
    if (r.IsBetPlaced === false) {
      // A bet that dies at the book is invisible in a funnel that only records
      // successes — and a rejection right after our add is the strongest possible
      // signal that we handed the user something unbettable.
      if (this.onRejected) {
        const slip = Array.isArray(r.CheckedStakesList) ? r.CheckedStakesList : [];
        const reason = _declineReason(r);
        this.onRejected({
          stake_count: slip.length,
          ours_leg_count: slip.filter(
            (s) => s?.StakeId != null && this.ours.has(Number(s.StakeId)),
          ).length,
          reason_code: reason,
        });
      }
      return;
    }
    // Neither an explicit success nor an explicit decline — a sibling endpoint
    // on the same path with a shape we don't recognize. Not a receipt, so don't
    // emit a placement for it.
    if (r.IsBetPlaced !== true) return;
    this.emitPlacement(url, r);
  }

  private emitPlacement(url: string, r: BettingResponse): void {
    const now = Date.now();
    // The receipt consumes its legs: their disappearance from the NEXT coupon
    // read is the bet being placed, not the user dropping the pick.
    if (Array.isArray(r.CheckedStakesList)) {
      for (const s of r.CheckedStakesList) {
        const id = Number(s?.StakeId);
        if (Number.isFinite(id) && id > 0) this.placedIds.add(id);
        for (const l of Array.isArray(s?.BetBuilderStakes) ? s.BetBuilderStakes : []) {
          const lid = Number(l?.StakeId);
          if (Number.isFinite(lid) && lid > 0) this.placedIds.add(lid);
        }
      }
    }
    // Expire SURFACED stakes before classifying — a correlational claim must not
    // survive its window, and pruning only afterwards would let a promo we showed
    // an hour ago still count.
    //
    // `ours` is deliberately NOT pruned here. An edit reopens the SAME order
    // number and the funnel keeps the newest row per order, so classifying an edit
    // against an expired registry would silently move a bet we genuinely inserted
    // OUT of the attributed count and turnover — hours after the fact, invisibly.
    // Our own adds keep expiring on the existing end-of-placement schedule.
    this.pruneSurfaced(now);
    this.pruneNavigated(now);
    // Precedence is strict and runs STRONGEST FIRST: widget_add > surfaced >
    // shown > navigated. A leg we inserted on a match we also navigated to is `widget_add`
    // and is counted exactly once — otherwise sending the user to a match would
    // inflate every add we made there into two claims about the same bet.
    const legs: PlacedLeg[] = this.slip.map((leg) => {
      const added = this.ours.get(leg.stakeId);
      const surfaced = this.surfaced.get(leg.stakeId);
      const shownKey = added || surfaced
        ? null
        : outcomeKey(leg.fixtureId, leg.marketId, leg.stakeCode, leg.argument, leg.part);
      const shown = shownKey ? this.shown.get(shownKey) : undefined;
      // Only reached when the stake is in no stake- or outcome-keyed registry —
      // i.e. the user picked their own outcome on a match we sent them to.
      const nav = added || surfaced || shown || leg.fixtureId == null
        ? undefined
        : this.navigated.get(leg.fixtureId);
      // WHICH MARKET, stamped on every leg below regardless of source. It is a
      // property of the coupon leg, not of how the leg got there, so it must
      // not be attached in only one of the three branches — the user's OWN legs
      // are 88.5% of the book and are exactly the ones this question is about.
      const market = {
        ...(leg.marketId == null ? {} : { market_id: leg.marketId }),
        ...(leg.stakeCode == null ? {} : { stake_code: leg.stakeCode }),
        // `argument` is sent even when null: a moneyline HAS no line, and that
        // is a fact worth storing rather than a gap.
        ...(leg.marketId == null ? {} : { argument: leg.argument }),
        // ⛔ WHICH SUB-EVENT. A corners total and a goals total are both market 3,
        // so without this the stored leg cannot say which one was bet. Only on a
        // sub-event leg: a main-book receipt is byte-identical to before.
        ...(leg.part ? { part: leg.part } : {}),
      };
      const firstSeen = this.slipFirstSeen.get(leg.stakeId);
      const slipAge = firstSeen == null ? {} : {
        slip_age_s: Math.max(0, Math.round((now - firstSeen) / 1000)),
        ...(this.slipAgeFloors.has(leg.stakeId) ? { slip_age_floor: true as const } : {}),
      };
      const entry = this.slipEntry.get(leg.stakeId);
      const withEntry = { ...(entry ? { entry } : {}), ...this.favOf(leg.fixtureId ?? leg.eventId ?? null, entry) };
      const mine = added ?? surfaced;
      if (mine) {
        return {
          ...market,
          ...slipAge,
          ...withEntry,
          stake_id: leg.stakeId,
          accepted_odds: leg.factor,
          source: added ? "widget_add" as const : "surfaced" as const,
          suggestion_id: mine.suggestion_id,
          event_id: mine.event_id,
          rec_odds: mine.rec_odds,
          selection: mine.selection,
          dims: mine.dims,
        };
      }
      if (shown) {
        // Our pick, their price: the outcome is the one on the card, so it keeps
        // the card's selection — but `rec_odds` stays null, so the drift proxy
        // (which reads "our legs" off that field) is not widened by it.
        return {
          ...market,
          ...slipAge,
          ...withEntry,
          stake_id: leg.stakeId,
          accepted_odds: leg.factor,
          source: "shown" as const,
          suggestion_id: shown.suggestion_id,
          event_id: shown.event_id,
          rec_odds: null,
          selection: shown.selection,
          dims: shown.dims,
        };
      }
      if (nav) {
        // A navigated leg is a stake we did NOT recommend, so it must not inherit
        // the card's `selection` or `rec_odds` — that would report our pick as
        // though the user had taken it. Name it from the board instead, the same
        // way a leg the user found alone is named, and keep only what is true:
        // which card sent them, and to which match.
        const facts = this.lookupStake?.(leg.stakeId);
        return {
          ...market,
          ...slipAge,
          ...withEntry,
          stake_id: leg.stakeId,
          accepted_odds: leg.factor,
          source: "navigated" as const,
          suggestion_id: nav.suggestion_id,
          event_id: nav.event_id,
          rec_odds: null,
          selection: facts?.selection ?? null,
          dims: nav.dims,
        };
      }
      // Not ours — but the board we already hold may still know what this stake
      // IS, turning the user's own pick from an id-and-price into a readable leg.
      //
      // ⭐ The EVENT id is carried unconditionally, and it does not depend on the
      // board at all: `CheckedStakesList` states `EventId` on every coupon leg,
      // live or PRE-MATCH. Until now an organic leg was stored as a bare stake id
      // and a price, so 88.5% of the book's legs — and 94.8% of its turnover —
      // were anonymous, and "which EVENTS attract the money" was unanswerable
      // (measured 2026-08-27 over 3 days: 50,559 legs, 5,836 named). `lookupStake`
      // below still only resolves the LIVE board, so the NAME may be missing; the
      // identity no longer is, and concentration is countable without it.
      const own: PlacedLeg = { ...market, ...slipAge, ...withEntry, stake_id: leg.stakeId, accepted_odds: leg.factor };
      // ⛔ THE FIXTURE, not the sub-event. A corners leg states its sub-event's own
      // `EventId`, which no board names and no card was ever shown for — filed
      // under it, the bet is invisible to every per-match count on the backend
      // (the visit claim, event demand). Our own routes above already send the
      // card's fixture id; this makes the visitor's own leg agree with them.
      if (leg.fixtureId != null) own.event_id = String(leg.fixtureId);
      const facts = this.lookupStake?.(leg.stakeId);
      // The EVENT lookup covers what the stake lookup cannot: a rotated stake id,
      // and — the reason this exists — a PRE-MATCH fixture, which the live
      // catalogue has never held. Consulted whenever the coupon named an event.
      //
      // ⛔ A miss stamps NOTHING. If an unresolved leg defaulted to `is_live:
      // true` we would manufacture the exact finding we are trying to test:
      // "100% of the money is on live events" was measured over a window in
      // which the live board was the ONLY thing a leg could resolve against.
      const ev = leg.fixtureId != null ? this.lookupEvent?.(leg.fixtureId) ?? null : null;
      if (ev?.starts_at) own.starts_at = ev.starts_at;
      if (facts || ev) {
        own.selection = facts?.selection ?? own.selection ?? null;
        own.dims = {
          sport: normalizeSport(ev?.sport_en ?? facts?.sport_en),
          is_live: ev ? ev.is_live : !!facts?.is_live,
          bet_type: this.slip.length > 1 ? "acca" : "single",
          odds_band: oddsBand(leg.factor),
          has_stream: false, // not knowable for a leg we did not surface
          rank: -1,          // -1 = "not from a card of ours"
          legs: this.slip.length > 1 ? this.slip.length : 0,
          pool_band: "none",
          league: slugify(ev?.league_en ?? facts?.league_en),
          country: slugify(ev?.country_en ?? facts?.country_en),
        };
      }
      return own;
    });

    // `attribution` reads ONLY the legs we inserted, so its meaning is unchanged
    // by the surfaced signal and no promo-routed bet can inflate it.
    const oursLegs = legs.filter((l) => l.source === "widget_add");
    const surfacedLegs = legs.filter((l) => l.source === "surfaced");
    const navigatedLegs = legs.filter((l) => l.source === "navigated");
    const shownLegs = legs.filter((l) => l.source === "shown");
    const attribution: AttributionClass = oursLegs.length === 0
      ? "organic"
      : oursLegs.length === legs.length
        ? "ours_exact"
        : "ours_partial";

    // BetFactor is always 0 in the receipt — compute the combined ourselves. But a
    // surfaced leg means the operator's own boost is priced into the bet and NOT
    // into these factors, so the product would understate what the user received.
    const suppressCombined = surfacedLegs.length > 0;
    const priced = legs.filter((l) => typeof l.accepted_odds === "number");
    // A Bet Builder is priced as a whole (correlated legs), so Π(leg factors) is
    // wrong for it: report the item's own price when the coupon is ONE builder
    // item, and nothing when a builder is mixed with other legs.
    const bbFactors = this.slip.map((s) => s.bbFactor);
    const betBuilder = bbFactors.some((f) => f != null);
    const oneBuilder = betBuilder && bbFactors.every((f) => f === bbFactors[0]);
    const combined = suppressCombined || legs.length === 0
      ? null
      : betBuilder
        ? (oneBuilder ? bbFactors[0] : null)
        : priced.length === legs.length
          ? Number(priced.reduce((a, l) => a * (l.accepted_odds as number), 1).toFixed(4))
          : null;

    const lastTs = (source: PlacedLeg[], from: Map<number, OurStake>): number =>
      source.reduce((max, l) => {
        const o = from.get(l.stake_id);
        return o && o.ts > max ? o.ts : max;
      }, 0);
    const lastAddTs = lastTs(oursLegs, this.ours);
    const lastSurfacedTs = lastTs(surfacedLegs, this.surfaced);
    // Navigated legs are filed by FIXTURE, so sub-event legs look up their parent.
    const lastNavigatedTs = navigatedLegs.reduce((max, l) => {
      const leg = this.slip.find((s) => s.stakeId === l.stake_id);
      const o = leg?.fixtureId == null ? undefined : this.navigated.get(leg.fixtureId);
      return o && o.ts > max ? o.ts : max;
    }, 0);
    const lastShownTs = shownLegs.reduce((max, l) => {
      const leg = this.slip.find((s) => s.stakeId === l.stake_id);
      const k = leg ? outcomeKey(leg.fixtureId, leg.marketId, leg.stakeCode, leg.argument, leg.part) : null;
      const o = k ? this.shown.get(k) : undefined;
      return o && o.ts > max ? o.ts : max;
    }, 0);
    const claimed = oursLegs[0] ?? surfacedLegs[0] ?? shownLegs[0] ?? navigatedLegs[0];

    // PlaceBetPlus is always an edit; the session flag catches any other route.
    const isEdit = this.editSession || url.indexOf("PlaceBetPlus") >= 0;
    this.editSession = false;

    this.onPlaced({
      order_number: Number(r.OrderNumber) || 0,
      bet_amount: typeof r.BetAmount === "number" ? r.BetAmount : null,
      bet_type: typeof r.BetType === "number" ? r.BetType : null,
      bet_type_name: typeof r.BetTypeName === "string" ? r.BetTypeName : null,
      stake_count: typeof r.StakeCount === "number" ? r.StakeCount : legs.length,
      combined_odds: combined,
      combined_odds_suppressed: suppressCombined,
      is_edit: isEdit,
      attribution,
      ours_leg_count: oursLegs.length,
      surfaced_leg_count: surfacedLegs.length,
      navigated_leg_count: navigatedLegs.length,
      shown_leg_count: shownLegs.length,
      influenced: surfacedLegs.length > 0 || navigatedLegs.length > 0,
      bet_builder: betBuilder,
      legs,
      seconds_since_add: lastAddTs ? Math.round((now - lastAddTs) / 1000) : null,
      seconds_since_surfaced: lastSurfacedTs ? Math.round((now - lastSurfacedTs) / 1000) : null,
      seconds_since_navigated: lastNavigatedTs ? Math.round((now - lastNavigatedTs) / 1000) : null,
      seconds_since_shown: lastShownTs ? Math.round((now - lastShownTs) / 1000) : null,
      dims: claimed?.dims,
      event_id: oursLegs.length ? oursLegs[0].event_id : undefined,
      rec_type: oursLegs.length ? this.ours.get(oursLegs[0].stake_id)?.rec_type : undefined,
      suggestion_id: oursLegs.length ? oursLegs[0].suggestion_id : undefined,
    });

    // The placement consumed the slip. Our stake registry is NOT cleared: an edit
    // reopens the SAME order and must still recognise the legs as ours.
    this.slip = [];
    this.slipFirstSeen.clear();
    this.slipAgeFloors.clear();
    this.slipEntry.clear();
    this.prune(now);
  }

  private prune(now: number): void {
    for (const [id, o] of this.ours) {
      if (now - o.ts > OURS_TTL_MS) this.ours.delete(id);
    }
    this.pruneSurfaced(now);
    this.pruneNavigated(now);
    for (const [k, o] of this.shown) {
      if (now - o.ts > SHOWN_TTL_MS) this.shown.delete(k);
    }
    this.save();
  }

  private pruneSurfaced(now: number): void {
    for (const [id, o] of this.surfaced) {
      if (now - o.ts > SURFACED_TTL_MS) this.surfaced.delete(id);
    }
  }

  private pruneNavigated(now: number): void {
    for (const [id, o] of this.navigated) {
      if (now - o.ts > NAVIGATED_TTL_MS) this.navigated.delete(id);
    }
  }
}
